import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Avatar,
  Grid,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  CalendarToday as CalendarIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { CompanyEmployee } from '../../types/employee';
import employeeService from '../../services/employeeService';
import { useAuth } from '../../hooks/useAuth';
import ChangeEmailModal from '../User/ChangeEmailModal';

interface EmployeeDetailModalProps {
  open: boolean;
  employee: CompanyEmployee | null;
  onClose: () => void;
  onEdit: (employee: CompanyEmployee) => void;
  onEmailChanged?: () => void;
}

export const EmployeeDetailModal: React.FC<EmployeeDetailModalProps> = ({
  open,
  employee,
  onClose,
  onEdit,
  onEmailChanged,
}) => {
  const { user } = useAuth();
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  if (!employee) return null;

  // 自分自身の情報かどうかチェック
  const isOwnProfile = user && employee.user?.id === user.id;

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'company_admin':
        return 'primary';
      case 'hr':
        return 'secondary'; // HRは青系のセカンダリーカラー
      case 'employee':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              従業員詳細
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ mb: 3 }}>
          {/* 基本情報セクション */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mr: 3,
                bgcolor: employee.role === 'company_admin' ? 'primary.main' : employee.role === 'hr' ? 'secondary.main' : 'grey.400',
                fontSize: '2rem',
              }}
            >
              {employee.role === 'company_admin' ? (
                <CheckCircleIcon fontSize="large" />
              ) : employee.role === 'hr' ? (
                <WorkIcon fontSize="large" />
              ) : (
                <PersonIcon fontSize="large" />
              )}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                {employee.user.fullName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip
                  label={employeeService.getRoleLabel(employee.role)}
                  color={getRoleColor(employee.role) as any}
                  size="medium"
                />
                <Chip
                  label={employeeService.getStatusLabel(employee.status)}
                  color={getStatusColor(employee.status) as any}
                  size="medium"
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* 詳細情報グリッド */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    メールアドレス
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                      }}
                    >
                      {employee.user.email}
                    </Typography>
                    {isOwnProfile && (
                      <Button
                        onClick={() => setEmailModalOpen(true)}
                        size="small"
                        variant="text"
                        sx={{
                          fontSize: '0.75rem',
                          minWidth: 'fit-content',
                          px: 1,
                          py: 0.25,
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        変更
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    部署
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {employee.department || '未設定'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WorkIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    役職
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {employee.jobTitle || '未設定'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LoginIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    最終ログイン
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {employeeService.formatLastLogin(employee.user.lastLoginAt)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    登録日
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {employeeService.formatCreatedAt(employee.createdAt)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    従業員ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    #{employee.id}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          sx={{ minWidth: 100 }}
        >
          閉じる
        </Button>
        {isOwnProfile && (
          <Button
            onClick={() => setEmailModalOpen(true)}
            variant="outlined"
            color="secondary"
            startIcon={<EmailIcon />}
            sx={{ minWidth: 180 }}
          >
            メールアドレス変更
          </Button>
        )}
        <Button
          onClick={() => onEdit(employee)}
          variant="contained"
          color="primary"
          sx={{ minWidth: 100 }}
        >
          編集
        </Button>
      </DialogActions>

      {/* メールアドレス変更モーダル */}
      {isOwnProfile && (
        <ChangeEmailModal
          open={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
          currentEmail={employee.user?.email || ''}
          onSuccess={(newEmail) => {
            setEmailModalOpen(false);
            if (onEmailChanged) {
              onEmailChanged();
            }
          }}
        />
      )}
    </Dialog>
  );
};
